/**
 * NotificationSchedulerService
 *
 * Web equivalent of the mobile NotificationManager background tasks.
 * Runs periodic checks while the tab is open:
 *   - Every 6 hours: accommodation cancellation, passport & license expiry
 *   - Every 30 minutes: Gmail email scan (if user has granted access)
 *
 * All thresholds are read from the user's notification settings stored at
 *   users/{userId}/settings/notifications  (same path as mobile).
 *
 * Lifecycle:
 *   NotificationSchedulerService.start(userId, userEmail?) — call on sign-in
 *   NotificationSchedulerService.stop()                    — call on sign-out
 */

import { doc, getDoc, updateDoc, deleteField, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { TripService } from './trip.service';
import { AccommodationService } from './accommodation.service';
import { TravelDocumentService } from './travel-document.service';
import { NotificationService, NotificationType } from './notification.service';
import { EmailProcessingService } from './email-processing.service';
import { GmailService } from './gmail.service';
import { SubscriptionTier } from '../models/index';

// How often to run each check (milliseconds)
const DEADLINE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;  // 6 hours
const EMAIL_CHECK_INTERVAL_MS = 30 * 60 * 1000;       // 30 minutes

interface UserNotificationSettings {
    accommodationNotificationsEnabled: boolean;
    accommodationDaysBefore: number;
    passportNotificationsEnabled: boolean;
    passportMonthsBefore: number;
    licenseNotificationsEnabled: boolean;
    licenseMonthsBefore: number;
}

const SETTINGS_DEFAULTS: UserNotificationSettings = {
    accommodationNotificationsEnabled: true,
    accommodationDaysBefore: 1,
    passportNotificationsEnabled: true,
    passportMonthsBefore: 6,
    licenseNotificationsEnabled: true,
    licenseMonthsBefore: 6,
};

export class NotificationSchedulerService {
    private static _deadlineTimer: ReturnType<typeof setInterval> | null = null;
    private static _emailTimer: ReturnType<typeof setInterval> | null = null;
    private static _userId: string | null = null;
    private static _userEmail: string | null = null;

    /** Start all schedulers. Safe to call multiple times — stops previous instance first. */
    static start(userId: string, userEmail?: string | null): void {
        this.stop();

        this._userId = userId;
        this._userEmail = userEmail ?? null;

        // Run all deadline checks immediately on login, then every 6 hours
        this._runAllDeadlineChecks();
        this._deadlineTimer = setInterval(
            () => this._runAllDeadlineChecks(),
            DEADLINE_CHECK_INTERVAL_MS
        );

        // Periodic email scan (only if Gmail is configured)
        if (userEmail) {
            this._runEmailCheck();
            this._emailTimer = setInterval(
                () => this._runEmailCheck(),
                EMAIL_CHECK_INTERVAL_MS
            );
        }

        console.log(`🔔 NotificationScheduler started for user ${userId}`);
    }

    /** Stop all schedulers (call on sign-out). */
    static stop(): void {
        if (this._deadlineTimer !== null) { clearInterval(this._deadlineTimer); this._deadlineTimer = null; }
        if (this._emailTimer !== null) { clearInterval(this._emailTimer); this._emailTimer = null; }
        this._userId = null;
        this._userEmail = null;
        console.log('🔔 NotificationScheduler stopped');
    }

    // ─── Load user notification settings ────────────────────────

    private static async _getSettings(userId: string): Promise<UserNotificationSettings> {
        try {
            const snap = await getDoc(doc(db, `users/${userId}/settings/notifications`));
            if (snap.exists()) {
                return { ...SETTINGS_DEFAULTS, ...snap.data() } as UserNotificationSettings;
            }
        } catch (e) {
            console.warn('⚠️ Could not load notification settings, using defaults:', e);
        }
        return { ...SETTINGS_DEFAULTS };
    }

    // ─── Combined deadline checker ───────────────────────────────

    private static async _runAllDeadlineChecks(): Promise<void> {
        const userId = this._userId;
        if (!userId) return;
        try {
            const settings = await this._getSettings(userId);
            console.log('🔔 Running deadline notification checks…');

            if (settings.accommodationNotificationsEnabled) {
                await this.checkCancellationDeadlines(userId, settings.accommodationDaysBefore);
            }
            if (settings.passportNotificationsEnabled) {
                await this.checkDocumentExpiry(userId, 'passport', settings.passportMonthsBefore);
            }
            if (settings.licenseNotificationsEnabled) {
                await this.checkDocumentExpiry(userId, 'driving_license', settings.licenseMonthsBefore);
            }
            // Check tier expiry (auto-downgrade + warnings)
            await this._checkTierExpiry(userId);
        } catch (e: any) {
            if (e?.code === 'permission-denied') {
                // Auth token not yet ready (race condition at startup) — retry once after 15 s
                console.info('ℹ️ Deadline check deferred — Firestore auth not ready yet, will retry in 15s');
                setTimeout(() => {
                    if (this._userId === userId) this._runAllDeadlineChecks();
                }, 15_000);
            } else {
                console.warn('⚠️ Deadline check failed:', e);
            }
        }
    }

    // ─── Accommodation cancellation check ───────────────────────

    /**
     * Mirror of mobile NotificationManager._checkAccommodationNotifications().
     * Uses the user's accommodationDaysBefore setting.
     */
    static async checkCancellationDeadlines(userId: string, daysBefore?: number): Promise<void> {
        if (daysBefore === undefined) {
            const settings = await this._getSettings(userId);
            daysBefore = settings.accommodationDaysBefore;
        }

        const now = new Date();
        const cutoff = new Date(now.getTime() + daysBefore * 24 * 60 * 60 * 1000);
        const trips = await TripService.getUserTrips(userId);

        for (const trip of trips) {
            if (!trip.id) continue;
            let accommodations;
            try { accommodations = await AccommodationService.getAccommodations(trip.id); }
            catch { continue; }

            for (const accommodation of accommodations) {
                const deadline: Date | undefined =
                    accommodation.cancelUntil instanceof Date ? accommodation.cancelUntil :
                        (accommodation as any).cancellationDeadline instanceof Date ? (accommodation as any).cancellationDeadline :
                            undefined;

                if (!deadline || deadline <= now || deadline > cutoff) continue;

                const dedupeKey = `accommodation_${accommodation.id}_${deadline.getTime()}`;
                if (await NotificationService.notificationExists(userId, dedupeKey)) continue;

                const tripName = trip.name || 'your trip';
                const accName = accommodation.name || 'Accommodation';

                await NotificationService.createNotification({
                    userId,
                    type: NotificationType.ACCOMMODATION_CANCELLATION,
                    title: 'Cancellation Deadline Approaching',
                    message: `${accName} (${tripName}) – free cancellation until ${deadline.toLocaleDateString('en-GB')}. Only ${daysBefore} day${daysBefore === 1 ? '' : 's'} left!`,
                    read: false,
                    actionUrl: `/trips/${trip.id}`,
                    dedupeKey,
                    metadata: { tripId: trip.id, tripName, accommodationId: accommodation.id },
                });
                console.log(`🔔 Cancellation notification sent for "${accName}" in "${tripName}"`);
            }
        }
    }

    // ─── Document expiry check (passport & driving license) ─────

    /**
     * Mirror of mobile NotificationManager._checkPassportNotifications() and
     * _checkLicenseNotifications(). Fires once per document per expiry date
     * using a stable dedupeKey, when the expiry is within monthsBefore months.
     */
    static async checkDocumentExpiry(
        userId: string,
        docType: 'passport' | 'driving_license',
        monthsBefore?: number
    ): Promise<void> {
        if (monthsBefore === undefined) {
            const settings = await this._getSettings(userId);
            monthsBefore = docType === 'passport'
                ? settings.passportMonthsBefore
                : settings.licenseMonthsBefore;
        }

        const now = new Date();
        const cutoff = new Date(now);
        cutoff.setMonth(cutoff.getMonth() + monthsBefore);

        let documents;
        try { documents = await TravelDocumentService.getDocuments(userId); }
        catch (e) { console.warn('⚠️ Could not fetch travel documents:', e); return; }

        const relevant = documents.filter(d => d.documentType === docType);

        for (const document of relevant) {
            const expiry = new Date(document.expiryDate);

            // Already expired or outside the alert window — skip
            if (expiry <= now || expiry > cutoff) continue;

            const dedupeKey = `${docType}_${document.id}_${expiry.getTime()}`;
            if (await NotificationService.notificationExists(userId, dedupeKey)) continue;

            const typeLabel = docType === 'passport' ? 'Passport' : 'Driving License';
            // documentNumber is encrypted client-side — use the plain-text name fields for display
            const ownerName = [document.firstName, document.lastName].filter(Boolean).join(' ').trim();
            const message = ownerName
                ? `Your ${typeLabel.toLowerCase()} (${ownerName}) expires on ${expiry.toLocaleDateString('en-GB')}`
                : `Your ${typeLabel.toLowerCase()} expires on ${expiry.toLocaleDateString('en-GB')}`;

            await NotificationService.createNotification({
                userId,
                type: NotificationType.DOCUMENT_EXPIRY,
                title: `${typeLabel} Expiring Soon`,
                message,
                read: false,
                actionUrl: `/profile`,
                dedupeKey,
                metadata: { documentId: document.id, documentType: docType },
            });
            console.log(`🔔 ${typeLabel} expiry notification sent for ${ownerName || document.id}`);
        }
    }

    // ─── Periodic email scan ─────────────────────────────────────

    // ─── Tier expiry check ───────────────────────────────────────

    /**
     * Auto-downgrades the user to Free if their paid tier has expired
     * (after a 3-day grace period), or sends a warning if expiry is within 7 days.
     * Runs once per 6-hour cycle — harmless to call repeatedly.
     */
    private static async _checkTierExpiry(userId: string): Promise<void> {
        try {
            const userSnap = await getDoc(doc(db, 'users', userId));
            if (!userSnap.exists()) return;

            const data = userSnap.data()!;
            const tierId = (data.tier as string | undefined) ?? 'free';

            // Already on Free or no expiry configured — nothing to do.
            if (!tierId || tierId === SubscriptionTier.Free) return;
            const rawExpiry = data.tier_expires_at;
            if (!rawExpiry) return;

            let expiresAt: Date;
            if (rawExpiry instanceof Timestamp) {
                expiresAt = rawExpiry.toDate();
            } else {
                expiresAt = new Date(rawExpiry);
            }
            if (isNaN(expiresAt.getTime())) return;

            const now = new Date();
            const gracePeriodDays = 3;
            const msPerDay = 24 * 60 * 60 * 1000;
            const daysUntilExpiry = Math.floor((expiresAt.getTime() - now.getTime()) / msPerDay);
            const tierDisplayNames: Record<string, string> = {
                free: 'Free', pro: 'Pro', premium: 'Premium', family: 'Family',
            };
            const currentTierName = tierDisplayNames[tierId] ?? tierId;

            if (now > expiresAt) {
                // Check if past grace period
                const daysPastExpiry = Math.floor((now.getTime() - expiresAt.getTime()) / msPerDay);
                if (daysPastExpiry >= gracePeriodDays) {
                    console.log(`🔔 Tier expired ${daysPastExpiry} days ago — auto-downgrading ${userId} to Free`);
                    // Downgrade to Free (user can update their own document per Firestore rules)
                    await updateDoc(doc(db, 'users', userId), {
                        tier: SubscriptionTier.Free,
                        tier_expires_at: deleteField(),
                        tier_auto_downgraded_at: serverTimestamp(),
                        tier_auto_downgraded_from: tierId,
                    });
                    await NotificationService.sendTierExpiredNotification(userId, currentTierName as string);
                    console.log(`✅ Auto-downgrade complete for ${userId}`);
                } else {
                    console.log(`⏳ Tier expired but within grace period (${daysPastExpiry} days past expiry)`);
                }
            } else if (daysUntilExpiry <= 7) {
                // Warn at 7 days and 1 day remaining
                const threshold = daysUntilExpiry <= 1 ? 1 : daysUntilExpiry;
                await NotificationService.sendTierExpiryWarningNotification(userId, threshold);
                console.log(`🔔 Tier expiry warning sent: ${threshold} days remaining for ${userId}`);
            }
        } catch (e) {
            console.warn('⚠️ Tier expiry check failed:', e);
        }
    }

    private static async _runEmailCheck(): Promise<void> {
        const userId = this._userId;
        const userEmail = this._userEmail;
        if (!userId || !userEmail) return;
        try {
            // Skip silently if Gmail is not connected for this installation
            const gmailStatus = await GmailService.getConnectionStatus();
            if (!gmailStatus.isConnected) return;
            console.log('📧 Periodic email scan starting…');
            await EmailProcessingService.processEmails(userId, userEmail);
        } catch (e: any) {
            if (String(e).includes('auth') || String(e).includes('token') || String(e).includes('sign')) return;
            console.warn('⚠️ Periodic email scan failed:', e);
        }
    }
}

export default NotificationSchedulerService;
